import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Container, Form, Button, Spinner } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { EyeSlash, Eye } from "react-bootstrap-icons";
import {
  resetUserPassword,
  clearUserPasswordResetError,
  clearUserPasswordResetSuccess,
} from "./slice";
import PasswordRequirements from "../../components/WebApp/PasswordRequirements";
import "./resetPassword.css";
import { useApp } from "../../contexts/AppContext";
import { validatePassword } from "../../../utils/helperFunctions/inputValidations";

const ResetPasswordAuthenticated = () => {
  const dispatch = useDispatch();
  const {
    isUserPasswordResetLoading,
    userPasswordResetError,
    userPasswordResetSuccess,
  } = useSelector((state) => state.createAccount);
  const navigate = useNavigate();

  // State variables for password fields
  const [currentPassword, setCurrentPassword] = useState("");
  const [currentPasswordError, setCurrentPasswordError] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Handler for current password change
  const handleCurrentPasswordChange = (e) => {
    if (userPasswordResetError) {
      dispatch(clearUserPasswordResetError());
    }

    const value = e.target.value;
    setCurrentPassword(value);
    setCurrentPasswordError(""); // Clear error when user types
  };

  // Toggle for current password visibility
  const toggleCurrentPasswordVisibility = () => {
    setShowCurrentPassword(!showCurrentPassword);
  };

  useEffect(() => {
    // Clear errors on mount and unmount
    dispatch(clearUserPasswordResetError());
    dispatch(clearUserPasswordResetSuccess());

    return () => {
      dispatch(clearUserPasswordResetError());
      dispatch(clearUserPasswordResetSuccess());
    };
  }, [dispatch]);

  // Handle successful password reset
  useEffect(() => {
    if (userPasswordResetSuccess) {
      // Clear the success flag
      dispatch(clearUserPasswordResetSuccess());

      // Navigate to dashboard with success state
      navigate("/", {
        state: {
          showPasswordResetSuccess: true,
        },
      });
    }
  }, [userPasswordResetSuccess, navigate, dispatch]);

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setNewPassword(value);
    setPasswordError(
      !value
        ? "Password is required"
        : !validatePassword(value)
        ? "Invalid password"
        : ""
    );
  };

  const handleConfirmPasswordChange = (e) => {
    const value = e.target.value;
    setConfirmPassword(value);
    setConfirmPasswordError(
      !value
        ? "Please confirm your password"
        : value !== newPassword
        ? "Passwords do not match"
        : ""
    );
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleResetSubmit = async () => {
    // Validate current password first
    if (!currentPassword) {
      setCurrentPasswordError("Current password is required");
      return;
    }

    // Validate new password fields
    if (
      !newPassword ||
      !confirmPassword ||
      passwordError ||
      confirmPasswordError ||
      newPassword !== confirmPassword
    ) {
      return;
    }

    // Use the resetUserPassword thunk for authenticated users
    const passwordData = {
      new_password: newPassword,
      current_password: currentPassword,
    };

    try {
      await dispatch(resetUserPassword(passwordData)).unwrap();
      // Success is handled by the useEffect above
    } catch (error) {
      // Error is handled by Redux state
    }
  };

  const handleBack = () => {
    navigate("/user-info");
  };

  // Helper function to render error message with field highlighting
  const renderErrorMessage = (error) => {
    if (!error) return null;

    if (typeof error === "string") {
      return error;
    }

    if (typeof error === "object") {
      // Handle different error object structures
      if (error.message) {
        return error.message;
      }
      if (error.detail) {
        return error.detail;
      }

      // Handle field-specific errors like {current_password: ["Invalid password."]}
      const errorKeys = Object.keys(error);
      if (errorKeys.length > 0) {
        return errorKeys.map((fieldKey, index) => {
          const fieldError = error[fieldKey];
          let errorMessage = "";

          if (Array.isArray(fieldError) && fieldError.length > 0) {
            errorMessage = fieldError[0];
          } else if (typeof fieldError === "string") {
            errorMessage = fieldError;
          }

          // Format field name for display
          const fieldDisplayName = fieldKey
            .replace(/_/g, " ")
            .replace(/\b\w/g, (l) => l.toUpperCase());

          return (
            <div key={fieldKey}>
              <strong>{fieldDisplayName}:</strong> {errorMessage}
            </div>
          );
        });
      }

      if (Array.isArray(error) && error.length > 0) {
        return error[0];
      }

      // Fallback for other object structures
      return JSON.stringify(error);
    }

    return "An error occurred. Please try again.";
  };

  // Helper function to check if a specific field has an error
  const hasFieldError = (fieldName) => {
    if (!userPasswordResetError || typeof userPasswordResetError !== "object")
      return false;
    return userPasswordResetError.hasOwnProperty(fieldName);
  };

  // Helper function to get field-specific error message
  const getFieldError = (fieldName) => {
    if (!hasFieldError(fieldName)) return "";

    const fieldError = userPasswordResetError[fieldName];
    if (Array.isArray(fieldError) && fieldError.length > 0) {
      return fieldError[0];
    }
    if (typeof fieldError === "string") {
      return fieldError;
    }
    return "";
  };

  // Update field errors based on API response
  useEffect(() => {
    if (userPasswordResetError && typeof userPasswordResetError === "object") {
      // Set current password error if it exists in API response
      if (hasFieldError("current_password")) {
        setCurrentPasswordError(getFieldError("current_password"));
      }

      // Set new password error if it exists in API response
      if (hasFieldError("new_password")) {
        setPasswordError(getFieldError("new_password"));
      }
    }
  }, [userPasswordResetError]);

  return (
    <Container className="d-flex flex-column align-items-center py-5">
      <div className="w-100" style={{ maxWidth: "400px" }}>
        <div className="back-button">
          <Link to="/user-info" onClick={handleBack}>
            ← Back
          </Link>
        </div>

        <h1 className="page-title mb-4">Reset Password</h1>

        {/* Show error if any */}
        {userPasswordResetError && (
          <div className="alert alert-danger" role="alert">
            {renderErrorMessage(userPasswordResetError)}
          </div>
        )}

        <Form>
          <Form.Group className="mb-3">
            <Form.Label>Current Password*</Form.Label>
            <div className="password-input-wrapper">
              <Form.Control
                type="text"
                value={currentPassword}
                onChange={handleCurrentPasswordChange}
                isInvalid={
                  !!currentPasswordError || hasFieldError("current_password")
                }
                style={{
                  WebkitTextSecurity: showCurrentPassword ? "none" : "disc",
                  MozTextSecurity: showCurrentPassword ? "none" : "disc",
                  textSecurity: showCurrentPassword ? "none" : "disc",
                }}
                required
              />
              <span
                className="password-toggle-icon"
                onClick={toggleCurrentPasswordVisibility}
              >
                {showCurrentPassword ? (
                  <Eye size={16} />
                ) : (
                  <EyeSlash size={16} />
                )}
              </span>
            </div>
            <Form.Control.Feedback type="invalid">
              {currentPasswordError || getFieldError("current_password")}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>New Password*</Form.Label>
            <div className="password-input-wrapper">
              <Form.Control
                type="text"
                value={newPassword}
                onChange={handlePasswordChange}
                isInvalid={!!passwordError || hasFieldError("new_password")}
                style={{
                  WebkitTextSecurity: showPassword ? "none" : "disc",
                  MozTextSecurity: showPassword ? "none" : "disc",
                  textSecurity: showPassword ? "none" : "disc",
                }}
                required
              />
              <span
                className="password-toggle-icon"
                onClick={togglePasswordVisibility}
              >
                {showPassword ? <Eye size={16} /> : <EyeSlash size={16} />}
              </span>
            </div>
            <Form.Control.Feedback type="invalid">
              {passwordError || getFieldError("new_password")}
            </Form.Control.Feedback>
            {newPassword ? (
              <PasswordRequirements password={newPassword} />
            ) : null}
          </Form.Group>

          <Form.Group className="mb-4">
            <Form.Label>Confirm New Password*</Form.Label>
            <div className="password-input-wrapper">
              <Form.Control
                type="text"
                value={confirmPassword}
                onChange={handleConfirmPasswordChange}
                isInvalid={!!confirmPasswordError}
                style={{
                  WebkitTextSecurity: showConfirmPassword ? "none" : "disc",
                  MozTextSecurity: showConfirmPassword ? "none" : "disc",
                  textSecurity: showConfirmPassword ? "none" : "disc",
                }}
                required
              />
              <span
                className="password-toggle-icon"
                onClick={toggleConfirmPasswordVisibility}
              >
                {showConfirmPassword ? (
                  <Eye size={16} />
                ) : (
                  <EyeSlash size={16} />
                )}
              </span>
            </div>
            <Form.Control.Feedback type="invalid">
              {confirmPasswordError}
            </Form.Control.Feedback>
          </Form.Group>

          <Button
            variant="primary"
            className="w-100 primary-action-button"
            onClick={handleResetSubmit}
            disabled={
              !newPassword ||
              !confirmPassword ||
              !!passwordError ||
              !!confirmPasswordError ||
              !currentPassword ||
              isUserPasswordResetLoading
            }
          >
            {isUserPasswordResetLoading ? (
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
                className="loading-button-spinner"
              />
            ) : (
              "Confirm Reset"
            )}
          </Button>
        </Form>
      </div>
    </Container>
  );
};

export default ResetPasswordAuthenticated;
